import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import { alpha } from '@mui/material/styles';
import type { Components, Theme } from '@mui/material/styles';
import { createSvgIcon } from '@mui/material/utils';
import { GridSeparatorIcon, useGridRootProps } from '@mui/x-data-grid-pro';
import type { GridBaseIconProps } from '@mui/x-data-grid-pro';
import { createElement } from 'react';
import type { JSXElementConstructor, ReactElement } from 'react';
import type { CSSObject } from '@mui/system';
import type { PickersLocaleText } from '@mui/x-date-pickers';

export const MUI_X_5_COLUMN_MENU_CLASS = 'Dispatch-columnMenu5';

export const MUI_X_5_PICKER_LOCALE_TEXT: Partial<PickersLocaleText> = {
  fieldYearPlaceholder: ({ digitAmount }) => 'y'.repeat(digitAmount),
  fieldMonthPlaceholder: ({ contentType }) =>
    contentType === 'letter' ? 'mmmm' : 'mm',
  fieldDayPlaceholder: () => 'dd',
};

// MUI resolves every MuiDataGrid override for each grid slot, and most slots
// pass no ownerState.
function signatureOf(ownerState: object | undefined): unknown {
  return ownerState && 'signature' in ownerState
    ? ownerState.signature
    : undefined;
}

function hadV5Look(ownerState: object | undefined): boolean {
  return signatureOf(ownerState) === 'DataGridPro';
}

// MUI X 8 paints the header colour on the header block and again on each
// header cell and filler, so an edge that falls between pixels takes it twice.
// MUI X 7 painted it once, on each header row.
const PREMIUM_7_12_ROOT: CSSObject = {
  '& .MuiDataGrid-columnHeaderTitle': {
    lineHeight: 'inherit',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'transparent',
  },
  '& .MuiDataGrid-columnHeaders > [role="row"]': {
    backgroundColor: 'var(--DataGrid-t-header-background-base)',
  },
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--pinnedLeft):not(.MuiDataGrid-columnHeader--pinnedRight), & .MuiDataGrid-columnHeaders .MuiDataGrid-filler, & .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller:not(.MuiDataGrid-scrollbarFiller--pinnedRight)':
    {
      backgroundColor: 'transparent',
    },
};

const HEADER_5_HEIGHT = 30;

function gridRoot(theme: Theme): CSSObject {
  return {
    '& .MuiDataGrid-cell': {
      borderTop: 'none',
      borderBottom: '1px solid var(--DataGrid-rowBorderColor)',
    },
    // Rendered cells sat in a centred flex row; plain text kept the
    // ellipsis, which a flex row would drop.
    '& .MuiDataGrid-cell:has(> *)': {
      display: 'flex',
      alignItems: 'center',
      lineHeight: 'inherit',
    },
    '& .MuiDataGrid-cell:not(:has(> *))': {
      lineHeight: 'inherit',
      paddingTop: `calc((var(--height) - 1px - ${theme.typography.body2.lineHeight}em) / 2)`,
    },
    // MUI X 8 paints the header, and the pinned header cells over it, with
    // one variable; the pinned cells keep the pinned background they had.
    '& .MuiDataGrid-columnHeaders': {
      '--DataGrid-t-header-background-base': 'transparent',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: 50,
        borderBottom: '1px solid var(--DataGrid-rowBorderColor)',
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
        pointerEvents: 'none',
      },
    },
    '& .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedRight, & .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller--pinnedRight':
      {
        backgroundColor: 'var(--DataGrid-t-cell-background-pinned)',
      },
    '& .MuiDataGrid-columnHeaders .MuiDataGrid-row--borderBottom > *': {
      borderBottomColor: 'transparent',
    },
    '& .MuiDataGrid-virtualScrollerContent--overflowed .MuiDataGrid-row--lastVisible .MuiDataGrid-cell':
      {
        borderBottomColor: 'transparent',
      },
    '& .MuiDataGrid-filler > div': {
      borderTop: 'none',
    },
    '& .MuiDataGrid-columnSeparator--resizable:hover': {
      color: theme.palette.text.primary,
    },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-columnHeader:focus-within::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: `calc((100% - ${HEADER_5_HEIGHT}px) / 2)`,
      height: HEADER_5_HEIGHT,
      outline: `solid ${alpha(theme.palette.primary.main, 0.5)} 1px`,
      outlineOffset: -1,
      pointerEvents: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus::after': {
      outlineColor: theme.palette.primary.main,
    },
    '& .MuiDataGrid-columnHeader:focus .MuiDataGrid-columnSeparator, & .MuiDataGrid-columnHeader--siblingFocused .MuiDataGrid-columnSeparator':
      {
        opacity: 1,
      },
    '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortButton, & .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted):hover .MuiDataGrid-sortButton':
      {
        opacity: 1,
      },
    '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon':
      {
        opacity: 0,
      },
    '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted):hover .MuiDataGrid-sortIcon':
      {
        opacity: 0.5,
      },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      gap: 0,
    },
    '& .MuiDataGrid-menuIcon': {
      marginRight: -10,
    },
    '& .MuiDataGrid-menuIconButton': {
      fontSize: 20,
    },
  };
}

function gridLook(ownerState: object | undefined, theme: Theme): CSSObject {
  if (hadV5Look(ownerState)) {
    return gridRoot(theme);
  }
  return signatureOf(ownerState) === 'DataGridPremium' ? PREMIUM_7_12_ROOT : {};
}

// MUI X 8 types icon slots with its own `IconProps`, whose `color: string` is
// wider than a Material icon accepts, and types its own icons as returning a
// ReactNode. Both icons take every prop the grid passes and render an element.
const Separator5Icon = createSvgIcon(
  createElement('path', { d: 'M11 19V5h2v14z' }),
  'Separator'
) as JSXElementConstructor<GridBaseIconProps>;
const Separator8Icon =
  GridSeparatorIcon as JSXElementConstructor<GridBaseIconProps>;

function SeparatorIcon(props: GridBaseIconProps): ReactElement {
  const rootProps = useGridRootProps();
  return createElement(
    hadV5Look(rootProps) ? Separator5Icon : Separator8Icon,
    props
  );
}

export const MUI_X_5_LOOK: Components<Theme> = {
  MuiDataGrid: {
    defaultProps: {
      slots: { columnResizeIcon: SeparatorIcon },
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => gridLook(ownerState, theme),
      menu: {
        [`& .${MUI_X_5_COLUMN_MENU_CLASS}`]: {
          minWidth: 'auto',
          '& .MuiListItemIcon-root': { display: 'none' },
        },
      },
    },
  },
  MuiPickersArrowSwitcher: {
    defaultProps: {
      slotProps: {
        previousIconButton: { size: 'small' },
        nextIconButton: { size: 'small' },
        leftArrowIcon: { fontSize: 'medium' },
        rightArrowIcon: { fontSize: 'medium' },
      },
    },
  },
  MuiDateCalendar: {
    styleOverrides: {
      root: {
        height: 'auto',
        maxHeight: 358,
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: ({ theme }) => {
        const hover = alpha(
          theme.palette.action.active,
          theme.palette.action.hoverOpacity
        );
        return {
          backgroundColor: theme.palette.background.paper,
          '&:hover': { backgroundColor: hover },
          '@media (pointer: fine)': { '&:hover': { backgroundColor: hover } },
          '&:focus': {
            backgroundColor: hover,
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        };
      },
    },
  },
  MuiDateRangePickerDay: {
    styleOverrides: {
      day: {
        '&:not(.Mui-selected):not(:hover):not(:focus)': {
          backgroundColor: 'transparent',
        },
      },
      rangeIntervalDayHighlight: ({ theme }) => ({
        backgroundColor: alpha(theme.palette.primary.light, 0.6),
      }),
      rangeIntervalDayHighlightStart: { marginLeft: 1 },
      rangeIntervalDayHighlightEnd: { marginRight: 1 },
      startOfMonth: { marginLeft: 1 },
      endOfMonth: { marginRight: 1 },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        marginTop: 16,
        marginBottom: 8,
        minHeight: 30,
        maxHeight: 30,
      },
      labelContainer: {
        maxHeight: 30,
      },
    },
  },
};
